/**
 * BespokeBridge – Programmes Module API
 * All collections are root-level; documents are scoped to an org via the
 * orgId field. Queries use where('orgId','==',orgId) and sort client-side
 * to avoid composite-index dependencies.
 */
import {
  collection, doc, getDoc, getDocs, addDoc, updateDoc, deleteDoc,
  query, where, orderBy, serverTimestamp, Timestamp, limit, runTransaction, increment, arrayUnion, deleteField
} from 'firebase/firestore';
import { db, storage } from '../utils/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { createNotification, NOTIFICATION_TYPES } from './notificationsApi';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const rootCol = (name)     => collection(db, name);
const rootDoc = (name, id) => doc(db, name, id);
const now     = ()         => serverTimestamp();
const toArr   = (snap)     => snap.docs.map(d => ({ id: d.id, ...d.data() }));

const byCreatedDesc = (a, b) =>
  (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0);
const byCreatedAsc  = (a, b) =>
  (a.createdAt?.toMillis?.() ?? 0) - (b.createdAt?.toMillis?.() ?? 0);

/** Returns days since a Firestore Timestamp (or ISO string). */
export const daysSince = (ts) => {
  if (!ts) return 0;
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  return Math.floor((Date.now() - date.getTime()) / 86400000);
};

// ═══════════════════════════════════════════════════════════════════════════════
// TOOL 1 — GOVERNMENT SCHEMES
// ═══════════════════════════════════════════════════════════════════════════════

export const getSchemes = async (orgId, includeArchived = false) => {
  const q = query(rootCol('governmentProgrammes'), where('orgId', '==', orgId));
  const rows = toArr(await getDocs(q));
  return rows
    .filter(r => !r.programmeType || r.programmeType === 'government')
    .filter(r => includeArchived || r.status === 'active')
    .sort(byCreatedDesc);
};

export const getScheme = async (orgId, schemeId) => {
  const d = await getDoc(rootDoc('governmentProgrammes', schemeId));
  return d.exists() ? { id: d.id, ...d.data() } : null;
};

export const createScheme = async (orgId, data, createdBy) => {
  const ref = await addDoc(rootCol('governmentProgrammes'), {
    ...data, orgId, programmeType: 'government', status: 'active', createdBy, createdAt: now(), updatedAt: now()
  });
  return ref.id;
};

export const updateScheme = async (orgId, schemeId, data) => {
  await updateDoc(rootDoc('governmentProgrammes', schemeId), { ...data, updatedAt: now() });
};

export const archiveScheme = async (orgId, schemeId) => {
  await updateDoc(rootDoc('governmentProgrammes', schemeId), { status: 'archived', updatedAt: now() });
};

// ── Referrals ──────────────────────────────────────────────────────────────────
export const getSchemeReferrals = async (orgId, statusFilter = null) => {
  const q = query(rootCol('governmentProgrammesReferrals'), where('orgId', '==', orgId));
  const rows = toArr(await getDocs(q));
  return rows
    .filter(r => !statusFilter || r.status === statusFilter)
    .sort(byCreatedDesc)
    .map(r => ({ ...r, daysOpen: daysSince(r.createdAt) }));
};

export const getMemberReferrals = async (orgId, memberId) => {
  const q = query(rootCol('governmentProgrammesReferrals'), where('orgId', '==', orgId), where('memberId', '==', memberId));
  const rows = toArr(await getDocs(q));
  return rows.sort(byCreatedDesc);
};

export const submitSchemeReferral = async (orgId, memberId, memberName, schemeId, schemeName) => {
  const ref = await addDoc(rootCol('governmentProgrammesReferrals'), {
    orgId, memberId, memberName, schemeId, schemeName,
    status: 'referred', claimedBy: null, claimedByName: null, claimedAt: null,
    notes: [], createdAt: now(), updatedAt: now()
  });
  return ref.id;
};

export const claimSchemeReferral = async (orgId, referralId, userId, userName, followUpPlan) => {
  const update = {
    claimedBy: userId, claimedByName: userName, claimedAt: now(),
    status: 'in_progress', updatedAt: now(),
  };
  if (followUpPlan && followUpPlan.trim()) {
    update.notes = arrayUnion({
      text: followUpPlan.trim(), action: 'claimed',
      by: userName, createdAt: new Date().toISOString(),
    });
  }
  await updateDoc(rootDoc('governmentProgrammesReferrals', referralId), update);
};

export const updateReferralStatus = async (orgId, referralId, status, note, updatedBy, updatedByName) => {
  const update = { status, updatedAt: now() };
  if (note && note.trim()) {
    update.notes = arrayUnion({
      text: note.trim(), action: 'status_update', status,
      by: updatedByName || updatedBy || 'Admin',
      createdAt: new Date().toISOString(),
    });
  }
  if (status === 'approved' || status === 'rejected') {
    update.resolvedBy = updatedBy || null;
    update.resolvedAt = now();
  }
  await updateDoc(rootDoc('governmentProgrammesReferrals', referralId), update);
};

export const releaseReferralClaim = async (orgId, referralId) => {
  await updateDoc(rootDoc('governmentProgrammesReferrals', referralId), {
    claimedBy: null, claimedByName: null, claimedAt: null,
    status: 'referred', updatedAt: now()
  });
};

export const updateGovApplicationStatus = async (orgId, referralId, applicationStatus) => {
  await updateDoc(rootDoc('governmentProgrammesReferrals', referralId), {
    applicationStatus, updatedAt: now()
  });
};

export const addGovApplicationMessage = async (orgId, referralId, senderId, senderName, senderRole, text) => {
  await updateDoc(rootDoc('governmentProgrammesReferrals', referralId), {
    correspondence: arrayUnion({
      senderId, senderName, senderRole, text,
      sentAt: new Date().toISOString(),
    }),
    updatedAt: now()
  });
};

export const getVolunteerReferrals = async (orgId, volunteerId) => {
  const q = query(rootCol('governmentProgrammesReferrals'), where('orgId', '==', orgId), where('claimedBy', '==', volunteerId));
  const rows = toArr(await getDocs(q));
  return rows.map(r => ({ ...r, daysOpen: daysSince(r.createdAt) }));
};

// ═══════════════════════════════════════════════════════════════════════════════
// TOOL 2 — ORGANISATION PROGRAMMES
// ═══════════════════════════════════════════════════════════════════════════════

export const getProgrammes = async (orgId, includeArchived = false) => {
  const q = query(rootCol('organisationProgrammes'), where('orgId', '==', orgId));
  const rows = toArr(await getDocs(q));
  return rows
    .filter(r => r.type !== 'government_scheme')
    .filter(r => !r.programmeType || r.programmeType === 'organisation')
    .filter(r => includeArchived || ['active', 'waitlisted'].includes(r.status))
    .sort(byCreatedDesc);
};

export const getProgramme = async (orgId, programmeId) => {
  const d = await getDoc(rootDoc('organisationProgrammes', programmeId));
  return d.exists() ? { id: d.id, ...d.data() } : null;
};

export const createProgramme = async (orgId, data, createdBy) => {
  const ref = await addDoc(rootCol('organisationProgrammes'), {
    ...data, orgId, programmeType: 'organisation', status: 'active', requiresAssessment: true,
    facilitatorIds: data.facilitatorIds || [],
    createdBy, createdAt: now(), updatedAt: now()
  });
  return ref.id;
};

export const updateProgramme = async (orgId, programmeId, data) => {
  await updateDoc(rootDoc('organisationProgrammes', programmeId), { ...data, updatedAt: now() });
};

export const archiveProgramme = async (orgId, programmeId) => {
  await updateDoc(rootDoc('organisationProgrammes', programmeId), { status: 'archived', updatedAt: now() });
};

// ── Sessions ───────────────────────────────────────────────────────────────────
export const getProgrammeSessions = async (orgId, programmeId) => {
  const constraints = [where('orgId', '==', orgId)];
  if (programmeId) constraints.push(where('programmeId', '==', programmeId));
  const q = query(rootCol('organisationProgrammeSessions'), ...constraints);
  const rows = toArr(await getDocs(q));
  return rows.sort((a, b) => (a.date?.toMillis?.() ?? 0) - (b.date?.toMillis?.() ?? 0));
};

export const getMemberSessions = async (orgId, memberId) => {
  const q = query(rootCol('organisationProgrammeSessions'), where('orgId', '==', orgId), where('enrolledMemberIds', 'array-contains', memberId));
  return toArr(await getDocs(q));
};

export const getVolunteerSessions = async (orgId, volunteerId) => {
  const q = query(rootCol('organisationProgrammeSessions'), where('orgId', '==', orgId), where('facilitatorId', '==', volunteerId));
  const rows = toArr(await getDocs(q));
  return rows.sort((a, b) => (a.date?.toMillis?.() ?? 0) - (b.date?.toMillis?.() ?? 0));
};

export const generateSessionQRToken = (orgId, sessionId, userId) =>
  `sess-${orgId}-${sessionId}-${userId}-${Date.now().toString(36)}`;

export const getSessionQRCodeUrl = (token, size = 200) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(token)}&margin=10`;

export const createSession = async (orgId, data) => {
  const memberCap = data.member_capacity != null && data.member_capacity !== '' ? parseInt(data.member_capacity, 10) : null;
  const volCap    = data.volunteer_capacity != null && data.volunteer_capacity !== '' ? parseInt(data.volunteer_capacity, 10) : null;
  const ref = await addDoc(rootCol('organisationProgrammeSessions'), {
    ...data, orgId,
    enrolledMemberIds: data.enrolledMemberIds || [],
    attendance: {}, sessionNotes: [], status: 'scheduled',
    member_capacity: memberCap,
    volunteer_capacity: volCap,
    member_registered_count: 0,
    volunteer_registered_count: 0,
    attendee_tracking_enabled: data.attendee_tracking_enabled ?? false,
    createdAt: now(), updatedAt: now()
  });
  return ref.id;
};

export const updateSession = async (orgId, sessionId, data) => {
  await updateDoc(rootDoc('organisationProgrammeSessions', sessionId), { ...data, updatedAt: now() });
};

// ── Session Registrations (members & volunteers attending a session) ────────────

export const getSessionRegistrations = async (orgId, sessionId, statusFilter = null) => {
  const q = query(rootCol('sessionRegistrations'), where('orgId', '==', orgId), where('sessionId', '==', sessionId));
  const rows = toArr(await getDocs(q));
  const filtered = statusFilter ? rows.filter(r => r.status === statusFilter) : rows;
  return filtered.sort((a, b) => (a.registeredAt?.toMillis?.() ?? 0) - (b.registeredAt?.toMillis?.() ?? 0));
};

export const getUserSessionRegistration = async (orgId, sessionId, userId, registrantType) => {
  const q = query(
    rootCol('sessionRegistrations'),
    where('orgId', '==', orgId),
    where('sessionId', '==', sessionId),
    where('userId', '==', userId),
    where('registrantType', '==', registrantType)
  );
  const snap = await getDocs(q);
  return snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() };
};

export const registerForSession = async (orgId, sessionId, userId, userName, userEmail, registrantType = 'member', registeredBy = null) => {
  const sessionSnap = await getDoc(rootDoc('organisationProgrammeSessions', sessionId));
  if (!sessionSnap.exists()) throw new Error('Session not found');
  const session = { id: sessionSnap.id, ...sessionSnap.data() };

  const existing = await getUserSessionRegistration(orgId, sessionId, userId, registrantType);
  if (existing && existing.status !== 'CANCELLED') throw new Error('Already registered for this session');

  const allRegs = await getSessionRegistrations(orgId, sessionId);
  const confirmedOfType  = allRegs.filter(r => r.registrantType === registrantType && r.status === 'CONFIRMED').length;
  const waitlistedOfType = allRegs.filter(r => r.registrantType === registrantType && r.status === 'WAITLISTED').length;

  const capacity = registrantType === 'volunteer'
    ? (session.volunteer_capacity != null ? parseInt(session.volunteer_capacity, 10) : null)
    : (session.member_capacity    != null ? parseInt(session.member_capacity,    10) : null);

  const isConfirmed     = capacity === null || confirmedOfType < capacity;
  const waitlistPosition = isConfirmed ? null : waitlistedOfType + 1;
  const token = generateSessionQRToken(orgId, sessionId, userId);

  const regRef = await addDoc(rootCol('sessionRegistrations'), {
    orgId, sessionId,
    programmeId:   session.programmeId || '',
    userId, userName, userEmail: userEmail || '',
    registrantType,
    status:            isConfirmed ? 'CONFIRMED' : 'WAITLISTED',
    waitlist_position: waitlistPosition,
    qr_code_token:     token,
    registeredAt:  now(),
    registeredBy:  registeredBy || userId,
    promotedAt:    null,
    cancelledAt:   null, cancellationReason: null,
  });

  if (isConfirmed) {
    const counterField = registrantType === 'volunteer' ? 'volunteer_registered_count' : 'member_registered_count';
    await updateDoc(rootDoc('organisationProgrammeSessions', sessionId), { [counterField]: increment(1), updatedAt: now() });
  }

  return { id: regRef.id, status: isConfirmed ? 'CONFIRMED' : 'WAITLISTED', waitlist_position: waitlistPosition, qr_code_token: token };
};

export const cancelSessionRegistration = async (orgId, sessionId, registrationId, reason, cancelledBy) => {
  const regRef  = rootDoc('sessionRegistrations', registrationId);
  const regSnap = await getDoc(regRef);
  if (!regSnap.exists()) throw new Error('Registration not found');
  const reg = regSnap.data();

  await updateDoc(regRef, {
    status: 'CANCELLED', cancelledAt: now(),
    cancellationReason: reason || '', cancelledBy, updatedAt: now(),
  });

  if (reg.status === 'CONFIRMED') {
    const counterField = reg.registrantType === 'volunteer' ? 'volunteer_registered_count' : 'member_registered_count';
    await updateDoc(rootDoc('organisationProgrammeSessions', sessionId), { [counterField]: increment(-1), updatedAt: now() });

    const allRegs = await getSessionRegistrations(orgId, sessionId);
    const waitlisted = allRegs
      .filter(r => r.registrantType === reg.registrantType && r.status === 'WAITLISTED')
      .sort((a, b) => (a.waitlist_position || 0) - (b.waitlist_position || 0));

    if (waitlisted.length > 0) {
      const first = waitlisted[0];
      await updateDoc(rootDoc('sessionRegistrations', first.id), {
        status: 'CONFIRMED', waitlist_position: null, promotedAt: now(), updatedAt: now(),
      });
      await updateDoc(rootDoc('organisationProgrammeSessions', sessionId), { [counterField]: increment(1), updatedAt: now() });

      for (let i = 1; i < waitlisted.length; i++) {
        await updateDoc(rootDoc('sessionRegistrations', waitlisted[i].id), { waitlist_position: i, updatedAt: now() });
      }

      try {
        const sessionSnap = await getDoc(rootDoc('organisationProgrammeSessions', sessionId));
        const sessionTitle = sessionSnap.exists() ? (sessionSnap.data().title || 'session') : 'session';
        await addDoc(rootCol('notifications'), {
          orgId, recipientId: first.userId,
          recipientRole: first.registrantType === 'volunteer' ? 'volunteer' : 'member',
          type: 'session_waitlist_promoted', sessionId,
          message: `A spot has opened up — you have been confirmed for "${sessionTitle}".`,
          read: false, createdAt: now(),
        });
      } catch (_) {}
    }
  }
};

export const generateSessionQRCodes = async (orgId, sessionId) => {
  const regs = await getSessionRegistrations(orgId, sessionId, 'CONFIRMED');
  const updates = regs
    .filter(r => !r.qr_code_token)
    .map(r => {
      const token = generateSessionQRToken(orgId, sessionId, r.userId);
      return updateDoc(rootDoc('sessionRegistrations', r.id), { qr_code_token: token, updatedAt: now() })
        .then(() => { r.qr_code_token = token; });
    });
  await Promise.all(updates);
  return regs;
};

export const markAttendance = async (orgId, sessionId, memberId, present) => {
  const ref = rootDoc('organisationProgrammeSessions', sessionId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const attendance = snap.data().attendance || {};
  attendance[memberId] = present ? 'present' : 'absent';
  await updateDoc(ref, { attendance, updatedAt: now() });
};

export const addSessionNote = async (orgId, sessionId, note, authorId, authorName) => {
  const ref = rootDoc('organisationProgrammeSessions', sessionId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const notes = snap.data().sessionNotes || [];
  notes.push({ text: note, authorId, authorName, createdAt: new Date().toISOString() });
  await updateDoc(ref, { sessionNotes: notes, updatedAt: now() });
};

// ── Session Volunteer Sign-ups ─────────────────────────────────────────────────

/** Get all scheduled sessions that require volunteers (two equality wheres, no composite index needed) */
export const getSessionVolunteerOpportunities = async (orgId) => {
  const q = query(
    rootCol('organisationProgrammeSessions'),
    where('orgId', '==', orgId),
    where('volunteers_required', '==', true)
  );
  const rows = toArr(await getDocs(q));
  return rows
    .filter(r => r.status === 'scheduled')
    .sort((a, b) => (a.date?.toMillis?.() ?? 0) - (b.date?.toMillis?.() ?? 0));
};

/** Get a volunteer's signup doc for a session (null if not signed up) */
export const getSessionVolunteerSignup = async (orgId, sessionId, volunteerId) => {
  if (!volunteerId) return null;
  const ref = doc(db, 'sessionVolunteerSignups', `${sessionId}_${volunteerId}`);
  const snap = await getDoc(ref);
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

/** Volunteer signs up for a session — atomic with counter increment */
export const signUpAsSessionVolunteer = async (orgId, sessionId, volunteerId, volunteerName) => {
  const signupRef = doc(db, 'sessionVolunteerSignups', `${sessionId}_${volunteerId}`);
  const sessionRef = rootDoc('organisationProgrammeSessions', sessionId);
  await runTransaction(db, async (tx) => {
    const existing = await tx.get(signupRef);
    if (existing.exists()) throw new Error('Already signed up as a volunteer for this session.');
    tx.set(signupRef, { orgId, sessionId, volunteerId, volunteerName, signed_up_at: now() });
    tx.update(sessionRef, { volunteer_signup_count: increment(1), updatedAt: now() });
  });
};

/** Volunteer withdraws their session sign-up — atomic with counter decrement */
export const withdrawSessionVolunteerSignup = async (orgId, sessionId, volunteerId) => {
  const signupRef = doc(db, 'sessionVolunteerSignups', `${sessionId}_${volunteerId}`);
  const sessionRef = rootDoc('organisationProgrammeSessions', sessionId);
  await runTransaction(db, async (tx) => {
    const existing = await tx.get(signupRef);
    if (!existing.exists()) throw new Error('No volunteer signup found to withdraw.');
    tx.delete(signupRef);
    tx.update(sessionRef, { volunteer_signup_count: increment(-1), updatedAt: now() });
  });
};

// ── Assessment Queue ───────────────────────────────────────────────────────────
export const getAssessmentQueue = async (orgId) => {
  const activeStatuses = ['pending_assessment', 'assessment_scheduled', 'approved', 'waitlisted', 'enrolled', 'on_hold'];
  const q = query(rootCol('assessmentRequests'), where('orgId', '==', orgId));
  const rows = toArr(await getDocs(q));
  return rows
    .filter(r => activeStatuses.includes(r.status))
    .sort(byCreatedAsc)
    .map(r => ({ ...r, daysOpen: daysSince(r.createdAt), slaBreached: daysSince(r.createdAt) >= 5 }));
};

export const getMemberAssessmentRequests = async (orgId, memberId) => {
  const q = query(rootCol('assessmentRequests'), where('orgId', '==', orgId), where('memberId', '==', memberId));
  const rows = toArr(await getDocs(q));
  return rows.sort(byCreatedDesc);
};

export const getVolunteerProgrammeRequests = async (orgId, volunteerId) => {
  const q = query(rootCol('assessmentRequests'), where('orgId', '==', orgId), where('facilitatorId', '==', volunteerId));
  return toArr(await getDocs(q));
};

export const submitAssessmentRequest = async (orgId, memberId, memberName, programmeId, programmeName) => {
  const ref = await addDoc(rootCol('assessmentRequests'), {
    orgId, memberId, memberName, programmeId, programmeName,
    status: 'pending_assessment',
    claimedBy: null, claimedByName: null, claimedAt: null,
    declineReason: null, alternativeProgramme: null, assessmentDate: null,
    notes: [], createdAt: now(), updatedAt: now()
  });
  return ref.id;
};

export const claimAssessment = async (orgId, requestId, userId, userName) => {
  await updateDoc(rootDoc('assessmentRequests', requestId), {
    claimedBy: userId, claimedByName: userName, claimedAt: now(), updatedAt: now()
  });
};

export const releaseAssessmentClaim = async (orgId, requestId) => {
  await updateDoc(rootDoc('assessmentRequests', requestId), {
    claimedBy: null, claimedByName: null, claimedAt: null, updatedAt: now()
  });
};

export const updateAssessmentStatus = async (orgId, requestId, status, extra = {}) => {
  await updateDoc(rootDoc('assessmentRequests', requestId), {
    status, ...extra, updatedAt: now()
  });
};

export const updateOrgApplicationStatus = async (orgId, requestId, applicationStatus) => {
  await updateDoc(rootDoc('assessmentRequests', requestId), {
    applicationStatus, updatedAt: now()
  });
};

export const addOrgApplicationMessage = async (orgId, requestId, senderId, senderName, senderRole, text) => {
  await updateDoc(rootDoc('assessmentRequests', requestId), {
    correspondence: arrayUnion({
      senderId, senderName, senderRole, text,
      sentAt: new Date().toISOString(),
    }),
    updatedAt: now()
  });
};

export const uploadReferralDocument = async (collectionName, orgId, docId, file, uploadedByName) => {
  const path = `referralDocuments/${orgId}/${docId}/${Date.now()}_${file.name}`;
  const storageRef = ref(storage, path);
  const snapshot = await uploadBytes(storageRef, file);
  const url = await getDownloadURL(snapshot.ref);
  const docMeta = { name: file.name, url, size: file.size, uploadedByName, uploadedAt: new Date().toISOString() };
  await updateDoc(rootDoc(collectionName, docId), { submittedDocuments: arrayUnion(docMeta), updatedAt: now() });
  return docMeta;
};

export const reopenDeclinedApplication = async (orgId, requestId, reason, adminId, adminName) => {
  const ref = rootDoc('assessmentRequests', requestId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const history = snap.data().reopenHistory || [];
  history.push({ reason, reopenedBy: adminId, reopenedByName: adminName, reopenedAt: new Date().toISOString() });
  await updateDoc(ref, { status: 'pending_assessment', declineReason: null, reopenHistory: history, updatedAt: now() });
};

// ── Enrolments ─────────────────────────────────────────────────────────────────
export const getEnrolments = async (orgId, programmeId = null) => {
  const q = query(rootCol('enrolments'), where('orgId', '==', orgId));
  const rows = toArr(await getDocs(q));
  return rows
    .filter(r => !programmeId || r.programmeId === programmeId)
    .sort((a, b) => (b.enrolDate?.toMillis?.() ?? 0) - (a.enrolDate?.toMillis?.() ?? 0));
};

export const getMemberEnrolments = async (orgId, memberId) => {
  const q = query(rootCol('enrolments'), where('orgId', '==', orgId), where('memberId', '==', memberId));
  return toArr(await getDocs(q));
};

export const createEnrolment = async (orgId, memberId, memberName, programmeId, programmeName) => {
  const ref = await addDoc(rootCol('enrolments'), {
    orgId, memberId, memberName, programmeId, programmeName,
    status: 'enrolled', enrolDate: now(), progress: { currentWeek: 1, totalWeeks: 6 },
    attendanceHistory: [], createdAt: now()
  });
  return ref.id;
};

// ═══════════════════════════════════════════════════════════════════════════════
// TOOL 3 — JOB BOARD
// ═══════════════════════════════════════════════════════════════════════════════

export const getJobListings = async (orgId, status = 'live') => {
  const q = query(rootCol('jobListings'), where('orgId', '==', orgId));
  const rows = toArr(await getDocs(q));
  return rows
    .filter(r => status === 'all' || r.status === status)
    .sort(byCreatedDesc);
};

export const getVolunteerJobListings = async (orgId, volunteerId) => {
  const q = query(rootCol('jobListings'), where('orgId', '==', orgId), where('postedBy', '==', volunteerId));
  const rows = toArr(await getDocs(q));
  return rows.sort(byCreatedDesc);
};

export const createJobListing = async (orgId, data, volunteerId, volunteerName) => {
  const ref = await addDoc(rootCol('jobListings'), {
    ...data, orgId, status: 'pending_approval', postedBy: volunteerId, postedByName: volunteerName,
    applications: [], createdAt: now(), updatedAt: now()
  });
  // Notify admin of the new submission (non-blocking)
  createNotification(
    orgId, 'ADMIN',
    NOTIFICATION_TYPES.JOB_LISTING_SUBMITTED,
    'New Job Listing Request',
    `"${data.title || 'Untitled'}" submitted by ${volunteerName} — awaiting approval.`,
    { listingId: ref.id }
  ).catch(() => {});
  return ref.id;
};

export const updateJobListing = async (orgId, listingId, data) => {
  await updateDoc(rootDoc('jobListings', listingId), { ...data, updatedAt: now() });
};

export const moderateJobListing = async (orgId, listingId, approved, moderatorId, moderatorName, reason = '') => {
  await updateDoc(rootDoc('jobListings', listingId), {
    status: approved ? 'live' : 'rejected',
    moderatedBy: moderatorId, moderatedByName: moderatorName, moderationReason: reason,
    moderatedAt: now(), updatedAt: now()
  });
};

export const removeJobListing = async (orgId, listingId, reason, adminId) => {
  await updateDoc(rootDoc('jobListings', listingId), {
    status: 'removed', removalReason: reason, removedBy: adminId, removedAt: now(), updatedAt: now()
  });
};

// ── Job Applications ───────────────────────────────────────────────────────────
// Applications are stored as an embedded array on each jobListing document.
// Resumes and covering letters are stored in Firebase Storage under
//   jobApplications/{orgId}/{listingId}/{appId}/

const uploadAppFile = async (orgId, listingId, appId, file, slot) => {
  const ext = file.name.split('.').pop();
  const storageRef = ref(storage, `jobApplications/${orgId}/${listingId}/${appId}/${slot}.${ext}`);
  await uploadBytes(storageRef, file, { contentType: file.type });
  return getDownloadURL(storageRef);
};

export const submitJobApplication = async (
  orgId, listingId, listingTitle,
  memberId, memberName, memberEmail,
  coveringLetterFile, resumeFile,
  postedBy
) => {
  const appId = crypto.randomUUID();

  const [coveringLetterUrl, resumeUrl] = await Promise.all([
    uploadAppFile(orgId, listingId, appId, coveringLetterFile, 'covering-letter'),
    uploadAppFile(orgId, listingId, appId, resumeFile, 'resume'),
  ]);

  const entry = {
    id: appId, memberId, memberName, memberEmail, listingTitle,
    coveringLetterUrl, coveringLetterFileName: coveringLetterFile.name,
    resumeUrl, resumeFileName: resumeFile.name,
    status: 'applied', createdAt: Timestamp.now(),
  };

  await updateDoc(rootDoc('jobListings', listingId), { applications: arrayUnion(entry) });

  // Notify the job poster (volunteer bell) and always notify admin
  const notifTitle = 'New Job Application';
  const notifMsg   = `${memberName} has applied for "${listingTitle}".`;
  const notifData  = { listingId, appId, memberName };
  const promises   = [createNotification(orgId, 'ADMIN', NOTIFICATION_TYPES.JOB_APPLICATION_RECEIVED, notifTitle, notifMsg, notifData)];
  if (postedBy && postedBy !== 'ADMIN') {
    promises.push(createNotification(orgId, postedBy, NOTIFICATION_TYPES.JOB_APPLICATION_RECEIVED, notifTitle, notifMsg, notifData));
  }
  await Promise.all(promises);

  return appId;
};

export const getListingApplications = async (orgId, listingId) => {
  const snap = await getDoc(rootDoc('jobListings', listingId));
  if (!snap.exists()) return [];
  return (snap.data().applications || []).slice().sort(byCreatedDesc);
};

export const getMemberJobApplications = async (orgId, memberId) => {
  const q = query(rootCol('jobListings'), where('orgId', '==', orgId));
  const listings = toArr(await getDocs(q));
  const result = [];
  for (const listing of listings) {
    for (const app of (listing.applications || [])) {
      if (app.memberId === memberId) {
        result.push({ ...app, listingId: listing.id, organisation: listing.organisation });
      }
    }
  }
  return result.sort(byCreatedDesc);
};

export const getAllJobApplications = async (orgId) => {
  const q = query(rootCol('jobListings'), where('orgId', '==', orgId));
  const listings = toArr(await getDocs(q));
  const result = [];
  for (const listing of listings) {
    for (const app of (listing.applications || [])) {
      result.push({
        ...app,
        listingId: listing.id,
        listingTitle: listing.title,
        organisation: listing.organisation,
        postedBy: listing.postedBy,
        postedByName: listing.postedByName,
      });
    }
  }
  return result.sort(byCreatedDesc);
};

// ═══════════════════════════════════════════════════════════════════════════════
// TOOL 4 — SUPPORT NETWORK
// ═══════════════════════════════════════════════════════════════════════════════

// ── Volunteer Roles ────────────────────────────────────────────────────────────
// Roles are stored as fields on the volunteers document:
//   volunteerRoles: ['Facilitator', 'Mentor', ...]          — approved roles
//   pendingVolunteerRoles: { Facilitator: { bio, experience, availability, requestedAt } }
//   roleAvailability: { Facilitator: 'active', Mentor: 'unavailable' }
// Valid role values: 'Facilitator', 'Mentor', 'Befriender', 'Volunteer'

const ROLES_REQUIRING_APPROVAL = ['Facilitator', 'Mentor', 'Befriender'];

export const registerVolunteerRole = async (orgId, volunteerId, volunteerName, role, bio, experience, availability) => {
  if (ROLES_REQUIRING_APPROVAL.includes(role)) {
    await updateDoc(rootDoc('volunteers', volunteerId), {
      [`pendingVolunteerRoles.${role}`]: {
        bio: bio || '',
        experience: experience || '',
        availability: availability || 'active',
        requestedAt: now(),
      },
      updatedAt: now(),
    });
    createNotification(
      orgId, 'ADMIN',
      NOTIFICATION_TYPES.VOLUNTEER_ROLE_REQUEST,
      'New Volunteer Role Request',
      `${volunteerName} has requested to register as a ${role}. Review in Volunteer Profiles → Pending Role Approvals.`,
      { volunteerId, volunteerName, role }
    ).catch(() => {});
  } else {
    await updateDoc(rootDoc('volunteers', volunteerId), {
      volunteerRoles: arrayUnion(role),
      [`roleAvailability.${role}`]: availability || 'active',
      updatedAt: now(),
    });
  }
};

export const getPendingVolunteerRoles = async (orgId) => {
  const q = query(rootCol('volunteers'), where('orgId', '==', orgId));
  const rows = toArr(await getDocs(q));
  const result = [];
  rows.forEach(v => {
    const pendingMap = v.pendingVolunteerRoles || {};
    Object.entries(pendingMap).forEach(([role, pr]) => {
      result.push({
        id: `${v.id}_${role}`,
        volunteerId: v.id,
        volunteerName: v.displayName,
        type: role,
        bio: pr.bio,
        experience: pr.experience,
        availability: pr.availability,
        status: 'pending',
        createdAt: pr.requestedAt,
      });
    });
    (v.volunteerRoles || []).forEach(role => {
      result.push({
        id: `${v.id}_${role}`,
        volunteerId: v.id,
        volunteerName: v.displayName,
        type: role,
        availability: (v.roleAvailability || {})[role] || 'active',
        status: 'approved',
      });
    });
  });
  return result;
};

export const getApprovedVolunteersByType = async (orgId, type) => {
  const q = query(rootCol('volunteers'), where('orgId', '==', orgId));
  const rows = toArr(await getDocs(q));
  return rows.filter(v =>
    (v.volunteerRoles || []).includes(type) &&
    v.status === 'active' &&
    ((v.roleAvailability || {})[type] || 'active') === 'active'
  );
};

export const getVolunteerRoles = async (orgId, volunteerId) => {
  const d = await getDoc(rootDoc('volunteers', volunteerId));
  if (!d.exists()) return [];
  const data = d.data();
  const roleAvailability = data.roleAvailability || {};
  const approved = (data.volunteerRoles || []).map(role => ({
    id: `${volunteerId}_${role}`,
    type: role,
    status: 'approved',
    availability: roleAvailability[role] || 'active',
  }));
  const pendingMap = data.pendingVolunteerRoles || {};
  const pending = Object.entries(pendingMap).map(([role, pr]) => ({
    id: `${volunteerId}_${role}`,
    type: role,
    bio: pr.bio,
    experience: pr.experience,
    availability: pr.availability,
    status: 'pending',
    createdAt: pr.requestedAt,
  }));
  return [...approved, ...pending];
};

export const approveVolunteerRole = async (orgId, syntheticId, status, adminId, reason = '') => {
  const idx = syntheticId.indexOf('_');
  const volunteerId = syntheticId.substring(0, idx);
  const role = syntheticId.substring(idx + 1);

  const volSnap = await getDoc(rootDoc('volunteers', volunteerId));
  const volData = volSnap.exists() ? volSnap.data() : {};
  const notifRecipient = volData.authUid || volunteerId;

  const updates = {
    [`pendingVolunteerRoles.${role}`]: deleteField(),
    updatedAt: now(),
  };
  if (status === 'approved') updates.volunteerRoles = arrayUnion(role);
  await updateDoc(rootDoc('volunteers', volunteerId), updates);

  await addDoc(rootCol('notifications'), {
    orgId,
    recipientId: notifRecipient,
    recipientRole: 'volunteer',
    type: status === 'approved' ? NOTIFICATION_TYPES.VOLUNTEER_ROLE_APPROVED : NOTIFICATION_TYPES.VOLUNTEER_ROLE_REJECTED,
    message: status === 'approved'
      ? `Great news! Your application to be a ${role} has been approved. Your role is now active.`
      : `Your application to be a ${role} was not approved at this time.${reason ? ' Reason: ' + reason : ''}`,
    read: false,
    createdAt: now(),
  });
};

export const updateVolunteerAvailability = async (orgId, syntheticId, availability) => {
  const idx = syntheticId.indexOf('_');
  const volunteerId = syntheticId.substring(0, idx);
  const role = syntheticId.substring(idx + 1);
  await updateDoc(rootDoc('volunteers', volunteerId), {
    [`roleAvailability.${role}`]: availability,
    updatedAt: now(),
  });
};

// ── Support Requests ───────────────────────────────────────────────────────────
export const submitSupportRequest = async (orgId, memberId, memberName, type) => {
  const ref = await addDoc(rootCol('supportRequests'), {
    orgId, memberId, memberName, type,
    status: 'pending_match', matchedVolunteerId: null, matchedVolunteerName: null,
    createdAt: now(), updatedAt: now()
  });
  if (type === 'widow_lifeline') {
    createNotification(
      orgId, 'ADMIN',
      NOTIFICATION_TYPES.BEFRIENDER_REQUEST,
      'New Widow-to-Widow Request',
      `${memberName} has requested a Befriender peer connection.`,
      { requestId: ref.id, memberId, memberName }
    ).catch(() => {});
  }
  return ref.id;
};

export const getPendingSupportRequests = async (orgId, type = null) => {
  const q = query(rootCol('supportRequests'), where('orgId', '==', orgId));
  const rows = toArr(await getDocs(q));
  return rows
    .filter(r => r.status === 'pending_match' && (!type || r.type === type))
    .sort(byCreatedAsc);
};

export const getMemberSupportRequests = async (orgId, memberId) => {
  const q = query(rootCol('supportRequests'), where('orgId', '==', orgId), where('memberId', '==', memberId));
  const rows = toArr(await getDocs(q));
  return rows.sort(byCreatedDesc);
};

// ── Care & Connect Groups (legacy) ─────────────────────────────────────────────
export const getCareConnectGroups = async (orgId) => {
  const q = query(rootCol('careConnectGroups'), where('orgId', '==', orgId));
  const rows = toArr(await getDocs(q));
  return rows.sort(byCreatedDesc);
};

export const getVolunteerCareGroups = async (orgId, volunteerId) => {
  const q = query(rootCol('careConnectGroups'), where('orgId', '==', orgId), where('facilitatorId', '==', volunteerId));
  return toArr(await getDocs(q));
};

export const createCareConnectGroup = async (orgId, data) => {
  const ref = await addDoc(rootCol('careConnectGroups'), {
    ...data, orgId, enrolledMembers: [], status: 'active', createdAt: now(), updatedAt: now()
  });
  return ref.id;
};

export const assignMemberToGroup = async (orgId, groupId, memberId, memberName) => {
  const ref = rootDoc('careConnectGroups', groupId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const enrolled = snap.data().enrolledMembers || [];
  enrolled.push({ memberId, memberName, joinedAt: new Date().toISOString() });
  await updateDoc(ref, { enrolledMembers: enrolled, updatedAt: now() });
};

// ── Support Network Groups (primary) ──────────────────────────────────────────
export const getSupportNetworkGroups = async (orgId) => {
  const q = query(rootCol('supportNetwork'), where('orgId', '==', orgId));
  const rows = toArr(await getDocs(q));
  return rows.sort(byCreatedDesc);
};

export const createSupportNetworkGroup = async (orgId, data) => {
  const ref = await addDoc(rootCol('supportNetwork'), {
    ...data, orgId,
    enrolledCount: (data.members || []).length,
    status: 'active', createdAt: now(), updatedAt: now(),
  });
  return ref.id;
};

export const updateSupportNetworkGroup = async (orgId, groupId, data) => {
  await updateDoc(rootDoc('supportNetwork', groupId), {
    ...data,
    enrolledCount: (data.members || []).length,
    updatedAt: now(),
  });
};

// ── Group Join Requests ────────────────────────────────────────────────────────

/**
 * Member submits a request to join a specific support group.
 * Stores in `groupJoinRequests` root collection; notifies admin.
 */
export const submitGroupJoinRequest = async (orgId, memberId, memberName, groupId, groupName) => {
  // Prevent duplicate pending requests for the same group
  const q = query(
    rootCol('groupJoinRequests'),
    where('orgId', '==', orgId),
    where('memberId', '==', memberId),
    where('groupId', '==', groupId)
  );
  const existing = await getDocs(q);
  const hasPending = existing.docs.some(d => d.data().status === 'pending');
  if (hasPending) throw new Error('You already have a pending request for this group.');

  const ref = await addDoc(rootCol('groupJoinRequests'), {
    orgId, memberId, memberName, groupId, groupName,
    status: 'pending',
    requestedAt: now(), updatedAt: now(),
  });

  createNotification(
    orgId, 'ADMIN',
    NOTIFICATION_TYPES.GROUP_REQUEST_SUBMITTED,
    'New Group Join Request',
    `${memberName} has requested to join "${groupName}".`,
    { requestId: ref.id, groupId }
  ).catch(() => {});

  return ref.id;
};

/** Admin: get all group join requests (optionally filtered by status). */
export const getGroupJoinRequests = async (orgId, statusFilter = null) => {
  const q = query(rootCol('groupJoinRequests'), where('orgId', '==', orgId));
  const rows = toArr(await getDocs(q));
  return rows
    .filter(r => !statusFilter || r.status === statusFilter)
    .sort(byCreatedDesc);
};

/** Member: get their own group join requests. */
export const getMemberGroupJoinRequests = async (orgId, memberId) => {
  const q = query(
    rootCol('groupJoinRequests'),
    where('orgId', '==', orgId),
    where('memberId', '==', memberId)
  );
  const rows = toArr(await getDocs(q));
  return rows.sort(byCreatedDesc);
};

/**
 * Admin approves a group join request.
 * Adds the member to the group's `members` array and sends a notification.
 */
export const approveGroupJoinRequest = async (orgId, requestId, adminId, adminName) => {
  const ref = rootDoc('groupJoinRequests', requestId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Request not found.');
  const req = snap.data();

  await updateDoc(ref, {
    status: 'approved',
    respondedBy: adminId, respondedByName: adminName,
    respondedAt: now(), updatedAt: now(),
  });

  // Add member to the group's members array
  const groupRef = rootDoc('supportNetwork', req.groupId);
  const groupSnap = await getDoc(groupRef);
  if (groupSnap.exists()) {
    const grp = groupSnap.data();
    const members = grp.members || [];
    if (!members.some(m => m.memberId === req.memberId)) {
      members.push({ memberId: req.memberId, memberName: req.memberName });
      await updateDoc(groupRef, { members, enrolledCount: members.length, updatedAt: now() });
    }
  }

  await createNotification(
    orgId, req.memberId,
    NOTIFICATION_TYPES.GROUP_REQUEST_APPROVED,
    'Group Request Approved',
    `Your request to join "${req.groupName}" has been approved! Welcome to the group.`,
    { requestId, groupId: req.groupId }
  );
};

/**
 * Admin declines a group join request and sends a notification.
 */
export const declineGroupJoinRequest = async (orgId, requestId, adminId, adminName, reason = '') => {
  const ref = rootDoc('groupJoinRequests', requestId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Request not found.');
  const req = snap.data();

  await updateDoc(ref, {
    status: 'declined',
    declineReason: reason,
    respondedBy: adminId, respondedByName: adminName,
    respondedAt: now(), updatedAt: now(),
  });

  await createNotification(
    orgId, req.memberId,
    NOTIFICATION_TYPES.GROUP_REQUEST_DECLINED,
    'Group Request Update',
    `Your request to join "${req.groupName}" was not approved at this time.${reason ? ' Reason: ' + reason : ''}`,
    { requestId, groupId: req.groupId }
  );
};

// ── Mentoring Pairs ────────────────────────────────────────────────────────────
export const getMentoringPairs = async (orgId) => {
  const q = query(rootCol('mentoringPairs'), where('orgId', '==', orgId));
  const rows = toArr(await getDocs(q));
  return rows.filter(r => r.status === 'active').sort(byCreatedDesc);
};

export const getMentorPairs = async (orgId, mentorId) => {
  const q = query(rootCol('mentoringPairs'), where('orgId', '==', orgId), where('mentorId', '==', mentorId));
  return toArr(await getDocs(q));
};

export const getMenteePairs = async (orgId, menteeId) => {
  const q = query(rootCol('mentoringPairs'), where('orgId', '==', orgId), where('menteeId', '==', menteeId));
  return toArr(await getDocs(q));
};

export const createMentoringPair = async (orgId, mentorId, mentorName, menteeId, menteeName) => {
  const ref = await addDoc(rootCol('mentoringPairs'), {
    orgId, mentorId, mentorName, menteeId, menteeName,
    status: 'active', startDate: now(), endDate: null, milestones: [], createdAt: now()
  });
  return ref.id;
};

export const logMentoringMilestone = async (orgId, pairId, note, authorId) => {
  const ref = rootDoc('mentoringPairs', pairId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const milestones = snap.data().milestones || [];
  milestones.push({ note, authorId, loggedAt: new Date().toISOString() });
  await updateDoc(ref, { milestones, updatedAt: now() });
};

export const endMentoringPair = async (orgId, pairId, reason, adminId) => {
  await updateDoc(rootDoc('mentoringPairs', pairId), {
    status: 'ended', endReason: reason, endedBy: adminId, endDate: now(), updatedAt: now()
  });
};

// ── Peer Connections ───────────────────────────────────────────────────────────
export const getPeerConnections = async (orgId) => {
  const q = query(rootCol('peerConnections'), where('orgId', '==', orgId));
  const rows = toArr(await getDocs(q));
  return rows.filter(r => r.status === 'active').sort(byCreatedDesc);
};

export const getPeerConnectionsByPeer = async (orgId, supporterId) => {
  const q = query(rootCol('peerConnections'), where('orgId', '==', orgId), where('supporterId', '==', supporterId));
  return toArr(await getDocs(q));
};

export const getPeerConnectionByMember = async (orgId, memberId) => {
  const q = query(rootCol('peerConnections'), where('orgId', '==', orgId), where('memberId', '==', memberId));
  const rows = toArr(await getDocs(q));
  return rows.find(r => r.status === 'active') || null;
};

export const createPeerConnection = async (orgId, supporterId, supporterName, memberId, memberName, requestId = null, supporterAuthUid = null) => {
  const ref = await addDoc(rootCol('peerConnections'), {
    orgId, supporterId, supporterName, memberId, memberName,
    status: 'active', startDate: now(), endDate: null, endReason: null, endedBy: null, createdAt: now()
  });
  if (requestId) {
    updateDoc(rootDoc('supportRequests', requestId), {
      status: 'matched',
      matchedVolunteerId: supporterId,
      matchedVolunteerName: supporterName,
      updatedAt: now(),
    }).catch(() => {});
  }
  // Notify the member they have been matched
  createNotification(
    orgId, memberId,
    NOTIFICATION_TYPES.BEFRIENDER_MATCHED_MEMBER,
    'You have been matched with a Befriender',
    `Great news! ${supporterName.split(' ')[0]} has been assigned as your Widow-to-Widow peer supporter. Visit your Support Network to connect.`,
    { connectionId: ref.id, supporterId, supporterName }
  ).catch(() => {});
  // Notify the volunteer — use their auth UID when available so the rule matches
  const volunteerRecipient = supporterAuthUid || supporterId;
  createNotification(
    orgId, volunteerRecipient,
    NOTIFICATION_TYPES.BEFRIENDER_MATCHED_VOLUNTEER,
    'New peer connection assigned',
    `You have been assigned as a Befriender for ${memberName.split(' ')[0]}. Visit your Support Network to connect.`,
    { connectionId: ref.id, memberId, memberName }
  ).catch(() => {});
  return ref.id;
};

export const endPeerConnection = async (orgId, connId, reason, adminId) => {
  await updateDoc(rootDoc('peerConnections', connId), {
    status: 'ended', endReason: reason, endedBy: adminId, endDate: now(), updatedAt: now()
  });
};

// ── Peer Messages (Widow-to-Widow conversation log) ───────────────────────────
export const getPeerMessages = async (orgId, connectionId) => {
  const q = query(rootCol('peerMessages'), where('orgId', '==', orgId), where('connectionId', '==', connectionId));
  const rows = toArr(await getDocs(q));
  return rows.sort(byCreatedAsc);
};

export const sendPeerMessage = async (orgId, connectionId, senderId, senderName, senderRole, text) => {
  await addDoc(rootCol('peerMessages'), {
    orgId, connectionId, senderId, senderName, senderRole, text, createdAt: now(),
  });
};

// ── Volunteer Availabilities ──────────────────────────────────────────────────
export const addVolunteerAvailabilitySlot = async (orgId, volunteerId, volunteerName, date, time, duration) => {
  const ref = await addDoc(rootCol('volunteerAvailabilities'), {
    orgId, volunteerId, volunteerName, date, time, duration,
    status: 'available', createdAt: now()
  });
  return ref.id;
};

export const getVolunteerAvailabilitySlots = async (orgId, volunteerId) => {
  const q = query(rootCol('volunteerAvailabilities'), where('orgId', '==', orgId), where('volunteerId', '==', volunteerId));
  const rows = toArr(await getDocs(q));
  return rows.sort((a, b) => new Date(`${a.date}T${a.time}`) - new Date(`${b.date}T${b.time}`));
};

export const getOpenAvailabilitySlots = async (orgId) => {
  const q = query(rootCol('volunteerAvailabilities'), where('orgId', '==', orgId));
  const rows = toArr(await getDocs(q));
  const nowTs = Date.now();
  return rows
    .filter(r => r.status === 'available' && new Date(`${r.date}T${r.time}`).getTime() > nowTs)
    .sort((a, b) => new Date(`${a.date}T${a.time}`) - new Date(`${b.date}T${b.time}`));
};

export const removeVolunteerAvailabilitySlot = async (orgId, slotId) => {
  await deleteDoc(rootDoc('volunteerAvailabilities', slotId));
};

// ── Appointments ──────────────────────────────────────────────────────────────
export const bookAppointment = async (orgId, slotId, volunteerId, volunteerName, memberId, memberName, date, time, duration) => {
  const apptRef = await addDoc(rootCol('appointments'), {
    orgId, volunteerId, volunteerName, memberId, memberName,
    availabilityId: slotId, date, time, duration,
    status: 'pending_approval', volunteerApprovedAt: null,
    createdAt: now(), updatedAt: now()
  });
  await updateDoc(rootDoc('volunteerAvailabilities', slotId), { status: 'booked', updatedAt: now() });
  await addDoc(rootCol('notifications'), {
    orgId, recipientId: volunteerId, recipientRole: 'volunteer',
    type: 'booking_request', appointmentId: apptRef.id,
    message: `${memberName.split(' ')[0]} has requested a ${duration}-min appointment on ${date} at ${time}.`,
    read: false, createdAt: now()
  });
  return apptRef.id;
};

export const getVolunteerAppointments = async (orgId, volunteerId) => {
  const q = query(rootCol('appointments'), where('orgId', '==', orgId), where('volunteerId', '==', volunteerId));
  const rows = toArr(await getDocs(q));
  return rows.sort((a, b) => new Date(`${a.date}T${a.time}`) - new Date(`${b.date}T${b.time}`));
};

export const getMemberAppointments = async (orgId, memberId) => {
  const q = query(rootCol('appointments'), where('orgId', '==', orgId), where('memberId', '==', memberId));
  const rows = toArr(await getDocs(q));
  return rows.sort((a, b) => new Date(`${a.date}T${a.time}`) - new Date(`${b.date}T${b.time}`));
};

export const respondToAppointment = async (orgId, appointmentId, approved) => {
  const snap = await getDoc(rootDoc('appointments', appointmentId));
  if (!snap.exists()) return;
  const appt = snap.data();
  await updateDoc(rootDoc('appointments', appointmentId), {
    status: approved ? 'confirmed' : 'declined',
    volunteerApprovedAt: approved ? now() : null,
    updatedAt: now()
  });
  if (!approved && appt.availabilityId) {
    await updateDoc(rootDoc('volunteerAvailabilities', appt.availabilityId), { status: 'available', updatedAt: now() });
  }
  await addDoc(rootCol('notifications'), {
    orgId, recipientId: appt.memberId, recipientRole: 'member',
    type: approved ? 'booking_confirmed' : 'booking_declined',
    appointmentId,
    message: approved
      ? `Your appointment on ${appt.date} at ${appt.time} has been confirmed!`
      : `Your appointment request on ${appt.date} at ${appt.time} was not approved. Please choose another time.`,
    read: false, createdAt: now()
  });
};

export const cancelAppointment = async (orgId, appointmentId, cancelledBy) => {
  const snap = await getDoc(rootDoc('appointments', appointmentId));
  if (!snap.exists()) return;
  const appt = snap.data();
  await updateDoc(rootDoc('appointments', appointmentId), {
    status: 'cancelled', cancelledBy, updatedAt: now()
  });
  if (appt.availabilityId) {
    await updateDoc(rootDoc('volunteerAvailabilities', appt.availabilityId), { status: 'available', updatedAt: now() });
  }
};

// ── Appointment Messages ──────────────────────────────────────────────────────
export const sendAppointmentMessage = async (orgId, appointmentId, senderId, senderName, senderRole, text) => {
  await addDoc(rootCol('appointmentMessages'), {
    orgId, appointmentId, senderId, senderName, senderRole, text, createdAt: now()
  });
};

export const getAppointmentMessages = async (orgId, appointmentId) => {
  const q = query(rootCol('appointmentMessages'), where('orgId', '==', orgId), where('appointmentId', '==', appointmentId));
  const rows = toArr(await getDocs(q));
  return rows.sort(byCreatedAsc);
};

// ── Notifications ─────────────────────────────────────────────────────────────
export const getNotifications = async (orgId, recipientId) => {
  const q = query(rootCol('notifications'), where('orgId', '==', orgId), where('recipientId', '==', recipientId));
  const rows = toArr(await getDocs(q));
  return rows.sort(byCreatedDesc);
};

export const markNotificationRead = async (orgId, notificationId) => {
  await updateDoc(rootDoc('notifications', notificationId), { read: true });
};

// ── Concerns ──────────────────────────────────────────────────────────────────
export const flagConcern = async (orgId, reporterId, reporterName, reporterRole, concernType, description, subjectId, subjectName) => {
  const ref = await addDoc(rootCol('concerns'), {
    orgId, reporterId, reporterName, reporterRole, concernType, description,
    subjectId, subjectName, status: 'open', adminNotes: '',
    resolvedBy: null, resolvedAt: null, createdAt: now()
  });
  return ref.id;
};

export const getConcerns = async (orgId) => {
  const q = query(rootCol('concerns'), where('orgId', '==', orgId));
  const rows = toArr(await getDocs(q));
  return rows.sort(byCreatedDesc);
};

export const resolveConcern = async (orgId, concernId, adminNotes, adminId) => {
  await updateDoc(rootDoc('concerns', concernId), {
    status: 'resolved', adminNotes, resolvedBy: adminId, resolvedAt: now(), updatedAt: now()
  });
};

// ═══════════════════════════════════════════════════════════════════════════════
// TOOL 5 — FINANCIAL ASSISTANCE & AWARDS
// ═══════════════════════════════════════════════════════════════════════════════

export const getFinancialCaseQueue = async (orgId) => {
  const q = query(rootCol('financialRequests'), where('orgId', '==', orgId));
  const rows = toArr(await getDocs(q));
  return rows
    .filter(r => ['submitted', 'under_review'].includes(r.status))
    .sort(byCreatedAsc)
    .map(r => ({ ...r, daysOpen: daysSince(r.createdAt), slaBreached: daysSince(r.createdAt) >= 5 }));
};

export const getMemberFinancialRequests = async (orgId, memberId) => {
  const q = query(rootCol('financialRequests'), where('orgId', '==', orgId), where('memberId', '==', memberId));
  const rows = toArr(await getDocs(q));
  return rows.sort(byCreatedDesc);
};

export const submitFinancialRequest = async (orgId, memberId, memberName, description, documentsUploaded = []) => {
  const ref = await addDoc(rootCol('financialRequests'), {
    orgId, memberId, memberName, type: 'hardship', description, documentsUploaded,
    status: 'submitted', claimedBy: null, claimedByName: null, claimedAt: null,
    declineReason: null, suggestedAlternative: null,
    approvedAmount: null, paymentMethod: null, disbursementNotes: null,
    createdAt: now(), updatedAt: now()
  });
  return ref.id;
};

export const claimFinancialCase = async (orgId, caseId, userId, userName) => {
  await updateDoc(rootDoc('financialRequests', caseId), {
    claimedBy: userId, claimedByName: userName, claimedAt: now(),
    status: 'under_review', updatedAt: now()
  });
};

export const releaseFinancialClaim = async (orgId, caseId) => {
  await updateDoc(rootDoc('financialRequests', caseId), {
    claimedBy: null, claimedByName: null, claimedAt: null,
    status: 'submitted', updatedAt: now()
  });
};

export const approveFinancialCase = async (orgId, caseId, amount, paymentMethod, notes, adminId, adminName) => {
  await updateDoc(rootDoc('financialRequests', caseId), {
    status: 'approved', approvedAmount: amount, paymentMethod, disbursementNotes: notes,
    approvedBy: adminId, approvedByName: adminName, approvedAt: now(), updatedAt: now()
  });
};

export const declineFinancialCase = async (orgId, caseId, reason, suggestedAlternative, adminId) => {
  await updateDoc(rootDoc('financialRequests', caseId), {
    status: 'declined', declineReason: reason, suggestedAlternative,
    declinedBy: adminId, declinedAt: now(), updatedAt: now()
  });
};

export const getFinancialHistory = async (orgId) => {
  const q = query(rootCol('financialRequests'), where('orgId', '==', orgId));
  const rows = toArr(await getDocs(q));
  return rows.sort(byCreatedDesc);
};

// ── Award Cycles ───────────────────────────────────────────────────────────────
export const getAwardCycles = async (orgId) => {
  const q = query(rootCol('awardCycles'), where('orgId', '==', orgId));
  const rows = toArr(await getDocs(q));
  return rows.sort((a, b) => (b.year ?? 0) - (a.year ?? 0));
};

export const getCurrentAwardCycle = async (orgId) => {
  const q = query(rootCol('awardCycles'), where('orgId', '==', orgId));
  const rows = toArr(await getDocs(q));
  return rows.find(r => r.status === 'open') || null;
};

export const createAwardCycle = async (orgId, year, openDate, closeDate, createdBy) => {
  const ref = await addDoc(rootCol('awardCycles'), {
    orgId, year, openDate, closeDate, status: 'draft',
    createdBy, createdAt: now()
  });
  return ref.id;
};

export const updateAwardCycleStatus = async (orgId, cycleId, status) => {
  await updateDoc(rootDoc('awardCycles', cycleId), { status, updatedAt: now() });
};

// ── Award Nominations ──────────────────────────────────────────────────────────
export const getAwardNominations = async (orgId, cycleId) => {
  const q = query(rootCol('awardNominations'), where('orgId', '==', orgId), where('cycleId', '==', cycleId));
  const rows = toArr(await getDocs(q));
  return rows.sort(byCreatedDesc);
};

export const getMemberNominations = async (orgId, memberId) => {
  const q = query(rootCol('awardNominations'), where('orgId', '==', orgId), where('memberId', '==', memberId));
  const rows = toArr(await getDocs(q));
  return rows.sort(byCreatedDesc);
};

export const submitAwardNomination = async (orgId, cycleId, year, memberId, memberName, childName, childAge, academicResults, supportingStatement) => {
  const ref = await addDoc(rootCol('awardNominations'), {
    orgId, cycleId, year, memberId, memberName, childName, childAge,
    academicResults, supportingStatement,
    status: 'submitted', adjudicationNotes: null, awardDetails: null,
    createdAt: now(), updatedAt: now()
  });
  return ref.id;
};

export const adjudicateNomination = async (orgId, nomId, awarded, notes, awardDetails, adminId) => {
  await updateDoc(rootDoc('awardNominations', nomId), {
    status: awarded ? 'awarded' : 'not_selected',
    adjudicationNotes: notes, awardDetails: awarded ? awardDetails : null,
    adjudicatedBy: adminId, adjudicatedAt: now(), updatedAt: now()
  });
};
